import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import {
  ChangeEvent,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useState,
} from 'react';

import { minFetchLength } from '../../store/addressSuggestionStore/constants/minFetchLength';
import { useAddressSuggestionSelection } from '../../store/addressSuggestionStore/hooks/useAddressSuggestionSelection';
import { AddressSuggestion as AddressSuggestionType } from '../../store/addressSuggestionStore/types/AddressSuggestion';
import useAddressSuggestionStore from '../../store/addressSuggestionStore/useAddressSuggestionStore';
import { AddressSuggestion } from '../AddressSuggestion/AddressSuggestion';
import { AddressLineOneProps } from './types/AddressLineOneProps';

export const AddressLineOne = ({
  errors,
  errorTestId,
  name,
  onKeyDown,
  register,
  setValue,
  suggestionFields,
  validation,
  ...textFieldProps
}: AddressLineOneProps): ReactNode => {
  const { onChange, ...registerProps } = register(name, validation);

  const { fetchAddressSuggestions, clearSuggestions } =
    useAddressSuggestionStore();

  const enableAddressSuggestions = Boolean(suggestionFields);

  const [disableBrowserAutoComplete, setDisableBrowserAutoComplete] =
    useState(false);

  // Prevents browser autofill dropdown from overlapping address suggestions.
  // Chrome ignores autocomplete="off" by design - using an unrecognized token is the
  // workaround recommended by the Chrome team: https://issues.chromium.org/issues/41163264#comment165
  const autoComplete = disableBrowserAutoComplete ? 'nope' : 'address-line1';

  const onSelect = useCallback(
    (suggestion: AddressSuggestionType): void => {
      const fieldOptions = {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      };

      setValue(name, suggestion.street_line, fieldOptions);

      const { cityFieldName, stateFieldName, postalCodeFieldName } =
        suggestionFields || {};

      if (cityFieldName) {
        setValue(cityFieldName, suggestion.city, fieldOptions);
      }

      if (postalCodeFieldName) {
        setValue(postalCodeFieldName, suggestion.zipcode, fieldOptions);
      }

      if (stateFieldName) {
        setValue(stateFieldName, suggestion.state, fieldOptions);
      }
    },
    [name, suggestionFields, setValue],
  );

  const { onSuggestionKeyDown, onSuggestionSelect } =
    useAddressSuggestionSelection(onSelect);

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onChange(event);

    if (enableAddressSuggestions) {
      const trimmedValue = event.target.value.trim();

      if (trimmedValue.length >= minFetchLength) {
        setDisableBrowserAutoComplete(true);

        fetchAddressSuggestions(trimmedValue, name);
      } else {
        setDisableBrowserAutoComplete(false);

        clearSuggestions();
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (enableAddressSuggestions) {
      onSuggestionKeyDown(event);
    }

    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <TextField
        {...registerProps}
        {...textFieldProps}
        autoComplete={autoComplete}
        error={!isEmpty(errors[name])}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />

      {enableAddressSuggestions && (
        <AddressSuggestion
          fieldName={name}
          onSuggestionSelect={onSuggestionSelect}
        />
      )}

      {!isEmpty(errors[name]) && (
        <span className="field-error" data-testid={errorTestId}>
          {toString(errors[name].message)}
        </span>
      )}
    </div>
  );
};
